import React, { Dispatch, SetStateAction, useRef } from 'react';

import { Drawer, Typography, IconButton, makeStyles } from '@material-ui/core';
import { Form, FormValues, yupSchemaToValidationFunc } from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { feedbackButtonZIndex } from 'styles/defaultTheme';
import { FormApi } from 'final-form';
import { useSelector, useDispatch } from 'react-redux';
import { useCapacityTypesNotDeletedData } from 'store/selectors';
import CloseIcon from '@material-ui/icons/Close';
import {
  addReserveCapacity,
  fetchReserveCapacities
} from 'store/slices/reserveCapacity';
import { DayOfWeek } from 'models/Adjustment';
import { DateTime } from 'luxon';
import { geographicAreasListSelector } from 'store/slices/geographicAreas';
import { GeographicAreaParent } from 'models/GeographicArea';
import { weekdays } from 'components/Forms/Weekdays';
import {
  ReserveCapacityFormFields,
  getReserveCapacityFormFields,
  validationSchema
} from './reserveCapacityFormConfig';

const useDrawerStyles = makeStyles(
  theme => ({
    drawer: {
      width: theme.containers.small,
      padding: theme.spacing(4, 3, 2, 3)
    },
    header: {
      display: 'flex',
      paddingBottom: theme.spacing(4),
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    },
    closeButton: {
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(3),
        cursor: 'pointer'
      },
      padding: 0
    },
    submitButton: {
      marginLeft: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(3)
    }
  }),
  { name: 'AddReservedCapacityDrawer' }
);

interface AddReservedCapacityDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  setCreatedId: Dispatch<SetStateAction<string | undefined>>;
}

export default function AddReservedCapacityDrawer({
  isOpen,
  toggleDrawer,
  setCreatedId
}: AddReservedCapacityDrawerProps) {
  const drawerClasses = useDrawerStyles();
  const formRef = useRef<FormApi>();
  const dispatch = useDispatch();

  const geographicAreas = useSelector(geographicAreasListSelector);
  const capacityTypes = useCapacityTypesNotDeletedData();

  const submitForm = async (values: FormValues) => {
    const selectedDays = Object.entries(
      (values[ReserveCapacityFormFields.EffectiveDays] as Record<
        string,
        boolean
      >) || {}
    )
      .filter(([, checked]) => checked)
      .map(([day]) => day as DayOfWeek);

    const formatDate = (date: DateTime) => date.toISODate();

    const createdId = await dispatch<Promise<string>>(
      addReserveCapacity({
        area: values[ReserveCapacityFormFields.Area] as string,
        capacityType: values[ReserveCapacityFormFields.CapacityType] as string,
        startDate: formatDate(
          values[ReserveCapacityFormFields.ValidFrom] as DateTime
        ) as string,
        endDate: formatDate(
          values[ReserveCapacityFormFields.ValidUntil] as DateTime
        ) as string,
        percentage: Number(values[ReserveCapacityFormFields.Percentage]),
        releaseAt: Number(values[ReserveCapacityFormFields.ReleaseAt]),
        days: selectedDays
      })
    );
    dispatch(fetchReserveCapacities());
    setCreatedId(createdId);
    toggleDrawer();
  };

  const handleDrawerSubmit = () => formRef.current?.submit();

  const reserveCapacityFields = getReserveCapacityFormFields(
    geographicAreas as GeographicAreaParent[],
    capacityTypes
  );

  const initialValues = {
    [ReserveCapacityFormFields.ValidFrom]: DateTime.now(),
    [ReserveCapacityFormFields.ValidUntil]: DateTime.now(),
    [ReserveCapacityFormFields.EffectiveDays]: weekdays.reduce(
      (acc, day) => ({ ...acc, [day]: true }),
      {}
    )
  };

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={toggleDrawer}
      classes={{
        paper: drawerClasses.drawer
      }}
      ModalProps={{
        style: {
          zIndex: feedbackButtonZIndex
        }
      }}
      data-testid="addReservedCapacityDrawer"
    >
      <header className={drawerClasses.header}>
        <Typography variant="h1">Add capacity</Typography>
        <IconButton
          aria-label="close"
          onClick={toggleDrawer}
          className={drawerClasses.closeButton}
          size="medium"
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </header>
      <Form
        validationFunc={yupSchemaToValidationFunc(validationSchema)}
        fields={reserveCapacityFields}
        onSubmit={submitForm}
        goBackToPreviousPage={false}
        initialValues={initialValues}
      >
        {({ form }) => {
          formRef.current = form;
          return null;
        }}
      </Form>
      <footer className={drawerClasses.footer}>
        <SecondaryButton
          size="medium"
          onClick={toggleDrawer}
          testId="addReservedCapacityCancel"
        >
          Cancel
        </SecondaryButton>
        <SubmitButton
          size="medium"
          onClick={handleDrawerSubmit}
          testId="addReservedCapacitySubmit"
          className={drawerClasses.submitButton}
        >
          Add capacity
        </SubmitButton>
      </footer>
    </Drawer>
  );
}
